import FAQAccordion from '@/components/FAQAccordion'
import Icon from '@/components/Icon'
import LandingPageHeader from '@/components/LandingPageHeader'
import Main from '@/components/Main'
import SEO from '@/components/SEO'
import styles from '@/styles/pages/_motivation-technologies.module.scss'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect } from 'react'

const Slider = dynamic(() => import('@/components/FlexibleContent/Slider'))
const Testimonials = dynamic(() => import('@/components/Testimonials'))
const TeamPhotoFeed = dynamic(() => import('@/components/FlexibleContent/TeamPhotoFeed'))

// Set when the press release URL is published; until then the secondary CTA is hidden.
const PRESS_RELEASE_URL = ''

const MOTECH_FAQ = [
  {
    question: 'When did the acquisition close?',
    answer: 'April 2026, with the combined teams fully operational July 1, 2026.'
  },
  {
    question: 'What does the combined company offer that neither could before?',
    answer:
      'The only integrated firm delivering both enterprise-grade behavioral learning platform capabilities and full-stack experience design, martech, creative, and behavioral systems services — from a single team, under one roof.'
  },
  {
    question: 'Where is the combined company based?',
    answer: 'St. Louis, Missouri — in the historic Tivoli Building in the Delmar Loop.'
  },
  {
    question: 'How do I get in touch?',
    answer: 'Use the <a href="/contact">contact form on our main site</a> or reach out directly through your existing account relationship.'
  }
]

export default function PageMotivationTechnologies(props) {
  const testimonialsRaw = props?.data?.page?.motechTestimonials?.testimonials
  const loopPhotosRaw = props?.data?.page?.motechLoopPhotos?.images
  const capabilityCardsRaw = props?.data?.page?.motechCapabilities?.cards

  // Dev-only signal so a developer immediately notices when an ACF group hasn't synced
  // (e.g. acf-json missing in this env, or the page ID changed).
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return
    if (!testimonialsRaw) console.warn('[motech] motechTestimonials missing from GraphQL response — ACF group may not be synced.')
    if (!loopPhotosRaw) console.warn('[motech] motechLoopPhotos missing from GraphQL response — ACF group may not be synced.')
    if (!capabilityCardsRaw) console.warn('[motech] motechCapabilities missing from GraphQL response — ACF group may not be synced.')
  }, [testimonialsRaw, loopPhotosRaw, capabilityCardsRaw])

  // Loading state for previews
  if (props.loading) {
    return null
  }

  const metaDesc = props?.data?.page?.seo?.metaDesc
  const { title, featuredImage } = props?.data?.page ?? { title: '' }
  const seoTitle = props?.data?.page?.seo?.title

  const testimonials = (testimonialsRaw || []).map((item) => ({
    quote: item.quote,
    name: item.name,
    title: item.title,
    headshot: item.headshot?.node
  }))

  const loopPhotos = (loopPhotosRaw || [])
    .map((item) => ({ headshot: { node: { sourceUrl: item.image?.node?.sourceUrl } } }))
    .filter((item) => item.headshot.node.sourceUrl)

  const capabilityCards = (capabilityCardsRaw || []).filter((card) => card?.cardIcon?.node?.sourceUrl)
  const capabilityFields = {
    eyebrowText: 'OUR CAPABILITIES',
    sliderTitle: 'What That Looks Like in Practice',
    sliderContent: '',
    card: capabilityCards
  }

  return (
    <>
      <SEO title={seoTitle || title} description={metaDesc} imageUrl={featuredImage?.node?.sourceUrl} />

      <LandingPageHeader logos={{ motech: { src: '/MotivationLogoBrand.svg', alt: 'Motivation Technologies' } }} />

      <Main>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <span className={styles.eyebrow}>A new chapter for Motivation Technologies</span>
            <h1 className={styles.heroTitle}>Motivation Technologies is now part of IntegrityXD.</h1>
            <p className={styles.subhead}>Same platform. Same team. Expanded capabilities. One St. Louis home.</p>
            <p className={styles.heroBody}>
              Motivation Technologies has joined IntegrityXD, bringing together two St. Louis companies with decades of proven success
              serving Fortune 100 and global enterprise clients. Your programs, your team, and your platform are continuing, now backed by a
              broader set of experience design and martech capabilities.
            </p>
            <Link className={styles.heroCta} href="/contact">
              <span>Talk to Our Team</span>
              <Icon icon={faArrowRight} />
            </Link>
          </div>
          <Image className={styles.stickerGroup} src="/StickerGroup.svg" alt="" aria-hidden="true" width={432} height={385} />
          <Image className={styles.stickerGroupMobile} src="/StickerGroup_Mobile.svg" alt="" aria-hidden="true" width={285} height={107} />
        </section>

        <section className={styles.unlocks}>
          <div className={`${styles.sectionInner} ${styles.unlocksInner}`}>
            <div className={styles.unlocksText}>
              <h2 className={styles.h2}>What this acquisition unlocks</h2>
              <p>
                Most firms deliver one layer of the enterprise engagement stack. A platform. Or a strategy practice. Or a creative shop.{' '}
                <strong>This deal delivers the full arc.</strong>
              </p>
              <p>
                Motivation Technologies built what most companies only promise: a behavioral learning platform that works at Fortune 100
                scale — 500,000+ users, 100+ countries, decades of measurable channel engagement for the world&rsquo;s leading enterprise
                brands.
              </p>
              <p>
                IntegrityXD spent 25 years building the experience design, marketing technology, and behavioral systems work that surrounds
                programs like that — the research, the strategy, the creative, and the measurement infrastructure that turns a platform into
                a compounding business asset.
              </p>
            </div>
            <div className={styles.unlocksMedia}>
              <Image
                src="/ComeSeeOurNewDigs.png"
                alt="IntegrityXD storefront in the Delmar Loop with a hand-lettered 'Come See Our New Digs' greeting from MoTech"
                width={611}
                height={469}
              />
            </div>
          </div>
        </section>

        <Testimonials testimonials={testimonials.slice(0, 1)} />

        <section className={styles.combined}>
          <div className={`${styles.sectionInner} ${styles.combinedInner}`}>
            <div className={styles.combinedText}>
              <span className={styles.sectionEyebrow}>The Combined Capability</span>
              <h2 className={styles.h2}>Why this combination makes sense</h2>
              <p>
                Experience design is fundamentally about shaping behavior. Motivation Technologies has been a leader in behavioral learning
                platforms for decades, powering programs across more than 100 countries for Fortune 100 retail channel networks. IntegrityXD
                has spent 25 years building the strategy, creative, marketing technology, and behavioral systems work that surrounds those
                programs.
              </p>
              <p>
                Together, the combined company can support enterprise clients across the full arc of behavior change from the
                customer-facing experience through the employee and channel partner ecosystems that power it.
              </p>
            </div>
            <div className={styles.infographic}>
              <Image
                src="/MoTech_Venn.svg"
                alt="Diagram of the combined IntegrityXD and Motivation Technologies capabilities"
                width={611}
                height={611}
              />
            </div>
          </div>
        </section>

        <Testimonials testimonials={testimonials.slice(1, 2)} />

        {capabilityCards.length > 0 && <Slider fields={capabilityFields} variant="compact" />}

        {loopPhotos.length > 0 ? (
          <TeamPhotoFeed
            centered
            fields={{
              teamFeedSubheading: 'One team. One location. The Delmar Loop.',
              teamFeedheading: 'We’re All in the Loop',
              teamFeedDescription:
                '<p>Effective July 1, 2026, the combined team operates from IntegrityXD’s offices in the historic Tivoli Building in the Delmar Loop, where a growing cluster of creative and technology businesses is anchored in one of St. Louis’s most distinctive neighborhoods.</p>',
              teamHeadshots: loopPhotos
            }}
          />
        ) : (
          <section className={styles.loop}>
            <div className={styles.sectionInner}>
              <span className={styles.sectionEyebrow}>One team. One location. The Delmar Loop.</span>
              <h2 className={styles.h2}>We&rsquo;re All in the Loop</h2>
              <p>
                Effective July 1, 2026, the combined team operates from IntegrityXD&rsquo;s offices in the historic Tivoli Building in the
                Delmar Loop, where a growing cluster of creative and technology businesses is anchored in one of St. Louis&rsquo;s most
                distinctive neighborhoods.
              </p>
            </div>
          </section>
        )}

        <FAQAccordion heading="FAQ" items={MOTECH_FAQ} />

        <section className={styles.finalCta}>
          <div className={styles.finalCtaInner}>
            <h2 className={styles.finalCtaTitle}>Let&rsquo;s talk about what&rsquo;s possible{' '}now.</h2>
            <p className={styles.finalCtaBody}>
              Whether you&rsquo;re an existing client with a question, a prospective client exploring options, or a partner navigating the
              transition — we&rsquo;d like to hear from you.
            </p>
            <div className={styles.ctaButtons}>
              <Link className={styles.btnPrimary} href="/contact">
                <span>Contact Us</span>
                <Icon icon={faArrowRight} />
              </Link>
              {PRESS_RELEASE_URL && (
                <a className={styles.btnSecondary} href={PRESS_RELEASE_URL}>
                  <span>Press Release</span>
                  <Icon icon={faArrowRight} />
                </a>
              )}
            </div>
          </div>
        </section>
      </Main>
    </>
  )
}
